import { AutopilotTaskDto, TaskCategoryTaskType } from '@urbanx/agx.activities.client';
import { BadgeColor, getDateTimeStatus } from '@urbanx/agx.ui-components2';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

dayjs.extend(advancedFormat);

// Pre-launch tasks have no due date until the launch date is set; show a
// neutral "Launch not set" badge instead of hiding the due date entirely.
export const getMissingDueDateText = (task?: AutopilotTaskDto) =>
  task?.category?.taskType === TaskCategoryTaskType.PreLaunch ? 'Launch not set' : undefined;

export interface DueDateBadgeState {
  text: string;
  color: BadgeColor;
  type: 'colourfill' | 'highlighted';
  highlightText?: string;
}

export const getStatusBadgeColor = (inProgress: boolean, isPending: boolean): BadgeColor => {
  if (isPending) return 'red';
  return inProgress ? 'blue' : 'green';
};

export const getDueDateBadgeState = (
  dueDate: string,
  isCompleted: boolean,
  completedDate?: Date | string,
  missingDateText?: string
): DueDateBadgeState => {
  const missingDateBadge: DueDateBadgeState = missingDateText
    ? {
        type: 'colourfill',
        color: 'lightgrey',
        text: missingDateText,
      }
    : {
        type: 'colourfill',
        color: 'red',
        text: 'Date not set',
      };

  if (isCompleted) {
    const parsedCompletedDate = dayjs(completedDate ?? dueDate);

    if (!parsedCompletedDate.isValid()) {
      return missingDateBadge;
    }

    return {
      type: 'colourfill',
      color: 'grey',
      text: `Completed ${parsedCompletedDate.format('h:mma Do MMM')}`,
    };
  }

  const status = getDateTimeStatus(dueDate ?? '');

  if (status === 'Invalid date') {
    return missingDateBadge;
  }

  const parsedDueDate = dayjs(dueDate);
  const formattedTime = parsedDueDate.format('h:mma');
  const formattedDate = parsedDueDate.format('Do MMM');
  const text = status === 'Today' ? formattedTime : `${formattedTime} ${formattedDate}`;
  const isHighlighted = ['Today', 'Overdue'].includes(status);
  const color = (
    status === 'Overdue' ? 'red' : status === 'Today' ? 'yellow' : 'orange'
  ) as BadgeColor;

  return isHighlighted
    ? {
        type: 'highlighted',
        color,
        text,
        highlightText: status,
      }
    : {
        type: 'colourfill',
        color,
        text,
      };
};
